'use memo';

import type { Dispatch, SetStateAction } from 'react';
import { CustomConditionModel, SpecialConditionData } from '@urbanx/agx.campaigns.client';
import { AgxButton, AgxSpacer, AgxTab, AgxTextInput, TabOption } from '@urbanx/agx.ui-components2';
import CustomSpecialCondition from './CustomSpecialCondition';
import { MandatorySpecialConditionDueDate } from './MandatorySpecialConditionDueDate';
import {
  CUSTOM_SPECIAL_CONDITION_NAME_MAX_LENGTH,
  CustomSpecialConditionDrafts,
  CustomSpecialConditionMode,
  getActiveCustomSpecialCondition,
  type CustomConditionUpdater,
  validateCustomSpecialCondition,
} from './customSpecialConditionEditor';

interface CustomSpecialConditionEditorProps {
  id: string;
  mode: CustomSpecialConditionMode;
  drafts: CustomSpecialConditionDrafts;
  usedClauseNumbers: number[];
  existingConditions: SpecialConditionData[];
  campaignId: string;
  triggerValidation: boolean;
  onModeChange: (mode: CustomSpecialConditionMode) => void;
  onDraftsChange: Dispatch<SetStateAction<CustomSpecialConditionDrafts>>;
}

const modeOptions = (mode: CustomSpecialConditionMode): TabOption[] => [
  {
    id: 'without-subclauses',
    label: 'No sub-clauses',
    selected: mode === 'without-subclauses',
  },
  {
    id: 'with-subclauses',
    label: 'With sub-clauses',
    selected: mode === 'with-subclauses',
  },
];

export function CustomSpecialConditionForm({
  id,
  mode,
  drafts,
  usedClauseNumbers,
  existingConditions,
  campaignId,
  triggerValidation,
  onModeChange,
  onDraftsChange,
}: CustomSpecialConditionEditorProps) {
  const activeCondition = getActiveCustomSpecialCondition(drafts, mode);
  const customCondition = activeCondition.customCondition;
  if (!customCondition) throw new Error('Custom special condition draft requires a model');

  const validation = validateCustomSpecialCondition(activeCondition, usedClauseNumbers);
  const showNameError =
    triggerValidation ||
    (customCondition.textInput?.trim().length ?? 0) > CUSTOM_SPECIAL_CONDITION_NAME_MAX_LENGTH;

  const updateActiveCondition = (
    updateCondition: (condition: SpecialConditionData) => SpecialConditionData
  ) => {
    onDraftsChange((currentDrafts) => {
      const updatedCondition = updateCondition(
        getActiveCustomSpecialCondition(currentDrafts, mode)
      );

      return mode === 'without-subclauses'
        ? { ...currentDrafts, withoutSubclauses: updatedCondition }
        : { ...currentDrafts, withSubclauses: updatedCondition };
    });
  };

  const updateCustomCondition = (updateCondition: CustomConditionUpdater) => {
    updateActiveCondition((currentCondition) => {
      if (!currentCondition.customCondition) {
        throw new Error('Custom special condition draft requires a model');
      }

      return {
        ...currentCondition,
        customCondition: updateCondition(currentCondition.customCondition),
      };
    });
  };

  const onClauseNumberChanged = (value: string) => {
    const clauseNumber = /^\d+$/.test(value) ? Number(value) : undefined;

    updateActiveCondition((currentCondition) => ({
      ...currentCondition,
      overriddenClauseNumber: clauseNumber,
    }));
  };

  const onNameChanged = (value: string) => {
    updateCustomCondition((currentCondition) => ({
      ...currentCondition,
      textInput: value,
    }));
  };

  const onDirectDueDateChanged = (overriddenDueDate: CustomConditionModel['overriddenDueDate']) => {
    updateCustomCondition((currentCondition) => ({
      ...currentCondition,
      overriddenDueDate,
      subClauses: [],
    }));
  };

  const onAddClause = () => {
    updateCustomCondition((currentCondition) => ({
      ...currentCondition,
      overriddenDueDate: undefined,
      subClauses: [
        ...(currentCondition.subClauses ?? []),
        {
          clauseId: window.crypto.randomUUID(),
          textInput: '',
        },
      ],
    }));
  };

  const onSubClauseChange = (
    clauseId: CustomConditionModel['clauseId'],
    updateSubClause: CustomConditionUpdater
  ) => {
    updateCustomCondition((currentCondition) => ({
      ...currentCondition,
      overriddenDueDate: undefined,
      subClauses: (currentCondition.subClauses ?? []).map((subClause) =>
        subClause.clauseId === clauseId ? updateSubClause(subClause) : subClause
      ),
    }));
  };

  const onSubClauseDelete = (clauseId: string) => {
    updateCustomCondition((currentCondition) => {
      if ((currentCondition.subClauses?.length ?? 0) <= 1) return currentCondition;

      return {
        ...currentCondition,
        subClauses: currentCondition.subClauses?.filter(
          (subClause) => subClause.clauseId !== clauseId
        ),
      };
    });
  };

  const tabs = modeOptions(mode);

  return (
    <AgxSpacer direction="vertical" gap="spacing-xl" fill>
      <AgxTab
        options={tabs}
        fill
        onChange={(tab) => onModeChange(tab.id as CustomSpecialConditionMode)}
      />
      <AgxTextInput
        key={`${activeCondition.id}-clause`}
        id={`${id}-clause-number`}
        type="number"
        label="Clause number"
        required
        hideOptionalText
        placeholder="Enter clause number"
        stretch
        defaultValue={activeCondition.overriddenClauseNumber?.toString() ?? ''}
        onChange={onClauseNumberChanged}
        errorText={triggerValidation ? validation.clauseNumberError : undefined}
      />
      <AgxTextInput
        key={`${activeCondition.id}-name`}
        id={`${id}-special-condition-name`}
        type="text"
        label="Special Condition Name"
        required
        hideOptionalText
        placeholder="Enter special condition name"
        stretch
        maxLength={CUSTOM_SPECIAL_CONDITION_NAME_MAX_LENGTH}
        defaultValue={customCondition.textInput ?? ''}
        onChange={onNameChanged}
        errorText={showNameError ? validation.nameError : undefined}
      />
      {mode === 'without-subclauses' ? (
        <MandatorySpecialConditionDueDate
          id={`${id}-direct-due-date`}
          dueDate={customCondition.overriddenDueDate}
          specialConditions={existingConditions}
          campaignId={campaignId}
          validate={triggerValidation}
          onChange={onDirectDueDateChanged}
        />
      ) : (
        <AgxSpacer direction="vertical" fill gap="xs">
          {(customCondition.subClauses ?? []).map((subClause, index) => (
            <CustomSpecialCondition
              key={subClause.clauseId}
              customCondition={subClause}
              onChange={(updateSubClause) => onSubClauseChange(subClause.clauseId, updateSubClause)}
              clausePosition={`${activeCondition.overriddenClauseNumber ?? ''}.${index + 1}`}
              specialConditions={existingConditions}
              campaignId={campaignId}
              onDelete={onSubClauseDelete}
              triggerValidation={triggerValidation}
              hideDeleteButton={(customCondition.subClauses?.length ?? 0) === 1}
            />
          ))}
          <AgxButton
            id={`${id}-add-clause-button`}
            text="Add Clause"
            variant="primary"
            size="medium"
            onClick={onAddClause}
          />
        </AgxSpacer>
      )}
    </AgxSpacer>
  );
}
